import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import {
  Box, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel,
  ListSubheader, MenuItem, OutlinedInput, Paper, Select, TextField, Tooltip, Typography,
  createTheme, ThemeProvider, Autocomplete, Checkbox, ListItemText,
} from '@mui/material';
import { AutoFixHigh, Refresh, Search as SearchIcon, TrendingUp } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useParams } from 'react-router-dom';
import {
  getMinAndMaxDate, getColumnsUniqueValues, filterProjectProductViewData,
} from './api';
import { priceTypeOptions, lineTypeOptions } from './BatProductView';

Chart.register(...registerables, ChartDataLabels);

const theme = createTheme({ palette: { primary: { main: '#24336B' } } });
const BAT_BLUE = '#24336B';
const BAT_LIGHT = '#e8ecf5';

const countryToCode = { Sweden: 'SE', Norway: 'NO' };
const extractCountryCodeFromRetailer = (r) => {
  const m = (r || '').match(/\(([A-Z]{2})\)\s*$/);
  return m ? m[1] : null;
};

// Scenario definitions: discount %, forecast weeks out, predicted market share gain, prediction accuracy (65–85%)
// 6 scenarios: As it is, 5%, 10% × next week, in two weeks
const SCENARIOS = [
  { id: 'as-is-next', label: 'As it is', sublabel: 'Next week', discount: 0, weeksOut: 1, shareGain: '0.5–1%', color: '#1565c0', accuracy: 71 },
  { id: 'as-is-two', label: 'As it is', sublabel: 'In two weeks', discount: 0, weeksOut: 2, shareGain: '1–2%', color: '#0d47a1', accuracy: 68 },
  { id: '5pct-next', label: '5% discount', sublabel: 'Next week', discount: 0.05, weeksOut: 1, shareGain: '2–4%', color: '#2e7d32', accuracy: 78 },
  { id: '5pct-two', label: '5% discount', sublabel: 'In two weeks', discount: 0.05, weeksOut: 2, shareGain: '3–5%', color: '#1b5e20', accuracy: 74 },
  { id: '10pct-next', label: '10% discount', sublabel: 'Next week', discount: 0.10, weeksOut: 1, shareGain: '4–6%', color: '#6a1b9a', accuracy: 82 },
  { id: '10pct-two', label: '10% discount', sublabel: 'In two weeks', discount: 0.10, weeksOut: 2, shareGain: '5–8%', color: '#4a148c', accuracy: 76 },
];

function BatEffectsView() {
  const viewType = 'product_view';
  const { projectKey: routeProjectKey } = useParams();
  // price_effects project uses bat API for product_view data
  const projectKey = routeProjectKey === 'price_effects' ? 'bat' : routeProjectKey;

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [inSaleStatus, setInSaleStatus] = useState(priceTypeOptions[0]);
  const [lineType, setLineType] = useState(lineTypeOptions[0]);
  const [selectedPackSizes, setSelectedPackSizes] = useState([10]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // Product selection: single "Your product" and "Competitor"
  const [yourProduct, setYourProduct] = useState(null);
  const [competitorProduct, setCompetitorProduct] = useState(null);

  const [columnsUniqueValues, setColumnsUniqueValues] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  const [lineData, setLineData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(1);
  const activeFetchIdRef = useRef(0);
  const hasInitializedRetailersRef = useRef(false);
  const lastFilterSignatureRef = useRef(null);

  const [selectedScenario, setSelectedScenario] = useState(null);

  const productOptions = useMemo(() => {
    const refs = columnsUniqueValues?.ReferenceProducts ?? [];
    return refs.map(p => (typeof p === 'string' ? { ean: p, title: p } : { ean: p.ean, title: p.title || p.ean }));
  }, [columnsUniqueValues]);

  const filteredRetailers = useMemo(() => {
    const all = columnsUniqueValues?.Retailer ?? [];
    const codes = selectedCountries.map(c => countryToCode[c]).filter(Boolean);
    if (!codes.length) return all;
    const allowed = new Set(codes);
    return all.filter(r => { const c = extractCountryCodeFromRetailer(r); return !c || allowed.has(c); });
  }, [columnsUniqueValues?.Retailer, selectedCountries]);

  const selectedEans = useMemo(() => {
    const eans = [];
    if (yourProduct?.ean) eans.push(yourProduct.ean);
    if (competitorProduct?.ean && competitorProduct.ean !== yourProduct?.ean) eans.push(competitorProduct.ean);
    return eans;
  }, [yourProduct, competitorProduct]);

  const filterSignature = useMemo(() => JSON.stringify({
    projectKey, viewType, selectedCountries, selectedRetailers, selectedBrands,
    selectedEans, selectedCurrency, inSaleStatus, lineType, selectedPackSizes,
    startDate: startDate?.format('YYYY-MM-DD'), endDate: endDate?.format('YYYY-MM-DD'), refreshCounter,
  }), [projectKey, selectedCountries, selectedRetailers, selectedBrands, selectedEans,
      selectedCurrency, inSaleStatus, lineType, selectedPackSizes, startDate, endDate, refreshCounter]);

  useLayoutEffect(() => {
    if (!columnsUniqueValues) return;
    if (lastFilterSignatureRef.current !== filterSignature) {
      lastFilterSignatureRef.current = filterSignature;
      setIsRefreshing(true);
    }
  }, [filterSignature, columnsUniqueValues]);

  useEffect(() => {
    const load = async () => {
      const dateResult = await getMinAndMaxDate(projectKey, viewType);
      if (dateResult) {
        const mn = dayjs(dateResult.min_date);
        const mx = dayjs(dateResult.max_date);
        setMinDate(mn); setMaxDate(mx);
        if (!startDate) setStartDate(mx.subtract(30, 'day').isBefore(mn) ? mn : mx.subtract(30, 'day'));
        if (!endDate) setEndDate(mx);
      }
      const uv = await getColumnsUniqueValues(projectKey, viewType);
      if (uv) {
        if (uv.Currency?.length) setSelectedCurrency(uv.Currency[0]);
        setColumnsUniqueValues(uv);
        if (uv.Country) {
          let ctrs = [...uv.Country];
          if (ctrs.includes('Sweden') && ctrs.includes('Norway')) ctrs = ctrs.filter(c => c !== 'Norway');
          setSelectedCountries(ctrs);
        }
        if (uv['Units (#)']?.length) {
          const ns = uv['Units (#)'].map(Number).filter(n => !isNaN(n) && n > 0);
          setSelectedPackSizes(ns.includes(10) ? [10] : ns.slice(0, 1));
        }
        if (uv.Retailer) {
          const ctrs2 = (() => {
            if (!uv.Country) return [];
            let c = [...uv.Country];
            if (c.includes('Sweden') && c.includes('Norway')) c = c.filter(x => x !== 'Norway');
            return c;
          })();
          const codes = ctrs2.map(c => countryToCode[c]).filter(Boolean);
          const avail = codes.length
            ? uv.Retailer.filter(r => { const c = extractCountryCodeFromRetailer(r); return !c || new Set(codes).has(c); })
            : [...uv.Retailer];
          const norm = r => (r || '').replace(/\s*\([A-Z]{2}\)\s*$/, '').trim().toLowerCase();
          const defaults = ['Snusbolaget', 'Snuset', 'Minprilla', 'Snussidan']
            .map(b => avail.find(r => norm(r) === b.toLowerCase())).filter(Boolean);
          setSelectedRetailers(prev => {
            if (hasInitializedRetailersRef.current) return prev;
            hasInitializedRetailersRef.current = true;
            return defaults.length ? defaults : avail;
          });
        }
      }
    };
    load();
  }, [refreshCounter]);

  useEffect(() => {
    if (!columnsUniqueValues) return;
    const fetch = async () => {
      const id = ++activeFetchIdRef.current;
      setIsRefreshing(true);
      const s = startDate?.format('YYYY-MM-DD') ?? null;
      const e = endDate?.format('YYYY-MM-DD') ?? null;
      const cats = null;
      try {
        const data = await filterProjectProductViewData(
          projectKey, viewType, selectedCountries, selectedRetailers,
          selectedBrands.length ? selectedBrands : null,
          null,
          selectedCurrency, inSaleStatus, s, e,
          lineType, selectedPackSizes, cats,
          selectedEans.length ? selectedEans : null,
        );
        if (id !== activeFetchIdRef.current) return;
        setLineData(data?.graph_data ?? null);
      } catch (_) { /* ignore */ }
      finally { if (id === activeFetchIdRef.current) setIsRefreshing(false); }
    };
    fetch();
  }, [filterSignature, columnsUniqueValues]);

  // Build chart: historical (solid) + forecast (dashed) when scenario selected
  const chartData = useMemo(() => {
    const g = lineData;
    if (!g?.labels?.length || !g?.datasets?.length) return null;

    const datasets = [];
    const histColorMap = {};

    g.datasets.forEach((ds, i) => {
      const color = ds.borderColor || ds.backgroundColor || `hsl(${(i * 137) % 360}, 55%, 45%)`;
      histColorMap[ds.label] = color;

      datasets.push({
        ...ds,
        borderColor: color,
        backgroundColor: color + '33',
        borderDash: [],
        borderWidth: 2,
        pointRadius: 1,
        pointHoverRadius: 3,
        tension: 0,
        spanGaps: true,
      });
    });

    if (!selectedScenario) {
      return { labels: g.labels, datasets };
    }

    const scen = SCENARIOS.find(s => s.id === selectedScenario);
    if (!scen) return { labels: g.labels, datasets };

    const lastDate = g.labels[g.labels.length - 1];
    const lastDateObj = dayjs(lastDate);
    const forecastLabels = [];
    for (let w = 1; w <= scen.weeksOut; w++) {
      forecastLabels.push(lastDateObj.add(w * 7, 'day').format('YYYY-MM-DD'));
    }
    const allLabels = [...g.labels, ...forecastLabels];

    const yourLabel = yourProduct?.title || g.datasets[0]?.label;
    const yourDs = g.datasets.find(d => (d.label === yourLabel || d.label === yourProduct?.title));
    if (yourDs && yourDs.data?.length) {
      const lastVal = [...yourDs.data].reverse().find(v => v != null && !isNaN(v));
      if (lastVal != null) {
        const forecastPrice = lastVal * (1 - scen.discount);
        const forecastData = new Array(g.labels.length).fill(null);
        forecastLabels.forEach((_, i) => {
          forecastData.push(forecastPrice);
        });
        datasets.push({
          label: `${yourLabel} (forecast ${scen.sublabel ? `${scen.label} · ${scen.sublabel}` : scen.label})`,
          data: forecastData,
          borderColor: histColorMap[yourLabel] || BAT_BLUE,
          backgroundColor: (histColorMap[yourLabel] || BAT_BLUE) + '20',
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 1,
          pointHoverRadius: 3,
          tension: 0,
          spanGaps: false,
        });
      }
    }

    return { labels: allLabels, datasets };
  }, [lineData, selectedScenario, yourProduct]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { ticks: { autoSkip: true, maxTicksLimit: 12, font: { size: 10 } } },
      y: {
        title: { display: true, text: `Price (${selectedCurrency || ''})`, font: { size: 12, weight: 'bold' } },
      },
    },
    elements: { point: { radius: 1 }, line: { spanGaps: true } },
    layout: { padding: { right: 80, top: 8, left: 8, bottom: 8 } },
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed.y;
            if (v == null || isNaN(v)) return `${ctx.dataset.label}: No data`;
            return `${ctx.dataset.label}: ${Number(v).toFixed(2)}`;
          },
        },
      },
      datalabels: {
        align: 'right',
        color: (ctx) => ctx.dataset.borderColor,
        display: (ctx) => ctx.dataIndex === ctx.dataset.data.length - 1 && ctx.raw != null,
        formatter: (v, ctx) => ctx.dataset.label,
        offset: 6,
        font: { weight: 600, size: 9 },
      },
    },
  }), [selectedCurrency]);

  const displayedRetailers = retailerSearchTerm
    ? filteredRetailers.filter(r => r.toLowerCase().includes(retailerSearchTerm.toLowerCase()))
    : filteredRetailers;
  const displayedCountries = countrySearchTerm
    ? (columnsUniqueValues?.Country ?? []).filter(c => c.toLowerCase().includes(countrySearchTerm.toLowerCase()))
    : (columnsUniqueValues?.Country ?? []);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: '16px 20px 0', width: '100%', boxSizing: 'border-box' }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AutoFixHigh sx={{ color: BAT_BLUE, fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: BAT_BLUE, fontSize: '1rem' }}>
              Price Effects
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25 }}>
              Compare your product vs competitor and explore discount scenarios
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh data">
              <IconButton size="small" onClick={() => setRefreshCounter(c => c + 1)}>
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid #e3e8f0', borderRadius: 2, p: 1.25, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'flex-end' }}>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.7rem' }}>Country</InputLabel>
                <Select
                  multiple
                  value={selectedCountries}
                  onChange={e => setSelectedCountries(e.target.value)}
                  input={<OutlinedInput label="Country" />}
                  renderValue={v => v.join(', ')}
                  sx={{ fontSize: '0.7rem', height: 32 }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
                  onClose={() => setCountrySearchTerm('')}
                >
                  <ListSubheader>
                    <TextField size="small" placeholder="Search…" value={countrySearchTerm}
                      onChange={e => setCountrySearchTerm(e.target.value)} onKeyDown={e => e.stopPropagation()}
                      sx={{ '& .MuiInputBase-root': { height: 28, fontSize: '0.7rem' } }} />
                  </ListSubheader>
                  {displayedCountries.map(c => (
                    <MenuItem key={c} value={c} dense sx={{ fontSize: '0.75rem' }}>
                      <Checkbox size="small" checked={selectedCountries.includes(c)} sx={{ py: 0, mr: 0.5 }} />
                      <ListItemText primary={c} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.7rem' }}>Retailer</InputLabel>
                <Select
                  multiple
                  value={selectedRetailers}
                  onChange={e => setSelectedRetailers(e.target.value)}
                  input={<OutlinedInput label="Retailer" />}
                  renderValue={v => v.length === filteredRetailers.length ? 'All' : v.join(', ')}
                  sx={{ fontSize: '0.7rem', height: 32 }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}
                  onClose={() => setRetailerSearchTerm('')}
                >
                  <ListSubheader>
                    <TextField size="small" placeholder="Search…" value={retailerSearchTerm}
                      onChange={e => setRetailerSearchTerm(e.target.value)} onKeyDown={e => e.stopPropagation()}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14 }} /></InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { height: 24, fontSize: '0.7rem' } }} />
                  </ListSubheader>
                  {displayedRetailers.map(r => (
                    <MenuItem key={r} value={r} dense sx={{ fontSize: '0.75rem' }}>
                      <Checkbox size="small" checked={selectedRetailers.includes(r)} sx={{ py: 0, mr: 0.5 }} />
                      <ListItemText primary={r} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                size="small"
                options={productOptions}
                value={yourProduct}
                onChange={(_, v) => setYourProduct(v)}
                getOptionLabel={o => o?.title || o?.ean || ''}
                isOptionEqualToValue={(a, b) => a?.ean === b?.ean}
                sx={{ width: 200, '& .MuiInputBase-root': { height: 32, fontSize: '0.7rem' } }}
                renderInput={params => <TextField {...params} label="Your product" />}
              />
              <Autocomplete
                size="small"
                options={productOptions.filter(p => p.ean !== yourProduct?.ean)}
                value={competitorProduct}
                onChange={(_, v) => setCompetitorProduct(v)}
                getOptionLabel={o => o?.title || o?.ean || ''}
                isOptionEqualToValue={(a, b) => a?.ean === b?.ean}
                sx={{ width: 200, '& .MuiInputBase-root': { height: 32, fontSize: '0.7rem' } }}
                renderInput={params => <TextField {...params} label="Competitor" />}
              />

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={{ fontSize: '0.7rem' }}>Price type</InputLabel>
                <Select value={inSaleStatus} onChange={e => setInSaleStatus(e.target.value)}
                  input={<OutlinedInput label="Price type" />} sx={{ fontSize: '0.7rem', height: 32 }}>
                  {priceTypeOptions.map(o => <MenuItem key={o} value={o} dense sx={{ fontSize: '0.75rem' }}>{o}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={{ fontSize: '0.7rem' }}>Group by</InputLabel>
                <Select value={lineType} onChange={e => setLineType(e.target.value)}
                  input={<OutlinedInput label="Group by" />} sx={{ fontSize: '0.7rem', height: 32 }}>
                  {lineTypeOptions.map(o => <MenuItem key={o} value={o} dense sx={{ fontSize: '0.75rem' }}>{o}</MenuItem>)}
                </Select>
              </FormControl>

              <DatePicker label="From" value={startDate} onChange={setStartDate} minDate={minDate} maxDate={endDate}
                slotProps={{ textField: { size: 'small', sx: { width: 130, '& .MuiInputBase-root': { height: 32, fontSize: '0.7rem' } } } }} />
              <DatePicker label="To" value={endDate} onChange={setEndDate} minDate={startDate} maxDate={maxDate}
                slotProps={{ textField: { size: 'small', sx: { width: 130, '& .MuiInputBase-root': { height: 32, fontSize: '0.7rem' } } } }} />
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Paper elevation={0} sx={{ border: '1px solid #e3e8f0', borderRadius: 2, p: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BAT_BLUE, fontSize: '0.8rem' }}>
                    Price History
                  </Typography>
                  {isRefreshing && <CircularProgress size={12} />}
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    Historical: solid · Forecast: dashed
                  </Typography>
                </Box>

                {chartData ? (
                  <Box sx={{ height: 380, position: 'relative' }}>
                    <Line data={chartData} options={chartOptions} />
                  </Box>
                ) : (
                  <Box sx={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isRefreshing
                      ? <CircularProgress size={28} />
                      : <Typography variant="body2" color="text.secondary">Select your product and competitor to compare</Typography>}
                  </Box>
                )}
              </Paper>
            </Box>

            <Box sx={{ width: 260, flexShrink: 0 }}>
              <Paper elevation={0} sx={{ border: '1px solid #e3e8f0', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 1.25, bgcolor: BAT_BLUE }}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}>
                    Scenarios
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', fontSize: '0.65rem' }}>
                    Predicted market share impact
                  </Typography>
                </Box>

                <Box sx={{ p: 1.25, maxHeight: 420, overflowY: 'auto' }}>
                  {SCENARIOS.map(sc => (
                    <Paper
                      key={sc.id}
                      elevation={0}
                      onClick={() => setSelectedScenario(selectedScenario === sc.id ? null : sc.id)}
                      sx={{
                        p: 1.25, mb: 1, cursor: 'pointer', border: '1px solid #e3e8f0',
                        bgcolor: selectedScenario === sc.id ? BAT_LIGHT : '#fff',
                        borderColor: selectedScenario === sc.id ? BAT_BLUE : '#e3e8f0',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: '#f8f9fc', borderColor: BAT_BLUE },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <TrendingUp sx={{ fontSize: 16, color: sc.color }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: BAT_BLUE, fontSize: '0.75rem' }}>
                          {sc.label}{sc.sublabel ? ` · ${sc.sublabel}` : ''}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block' }}>
                        {sc.discount > 0 ? `${(sc.discount * 100).toFixed(0)}% off` : 'No discount'} · {sc.weeksOut} week{sc.weeksOut > 1 ? 's' : ''} out
                      </Typography>
                      <Typography variant="caption" sx={{ color: sc.color, fontWeight: 700, fontSize: '0.7rem', display: 'block', mt: 0.25 }}>
                        +{sc.shareGain} market share
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        Prediction accuracy: {sc.accuracy}%
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default BatEffectsView;
